import {ChangeDetectorRef, Component, OnInit, ViewChild} from '@angular/core';
import {ConfigServiceProvider} from "../../services/config/config.service.provider";
import {ConfirmationService, MessageService} from "primeng/api";
import {UIChart} from "primeng/chart";
import {TranslateService} from "@ngx-translate/core";
import {CcBaseComponent} from "../../crystal-elements/cc-base.component";
import {MetricsService} from "../../services/metrics/metrics.service";
import {ClearingScorecard} from "../../model/metrics/clearing-scorecard";
import {RecordRequest} from "../../model/api/record-request";
import {SecurityType} from "../../model/metrics/security-type";
import {AppConfigService} from "../../services/app-config.service";
import {EMPTY, exhaustMap, takeUntil, timer} from "rxjs";
import {catchError} from "rxjs/operators";

@Component({
  selector: 'cc-summary-chart',
  templateUrl: './summary-chart.component.html',
  styleUrls: ['./summary-chart.component.scss'],
  providers: [
    ConfigServiceProvider, MessageService
  ]
})
export class SummaryChartComponent extends CcBaseComponent implements OnInit {

  @ViewChild('chart') chartRef: UIChart;

  chartData: any;
  chartOptions: any;

  currentRequest: RecordRequest = {};

  metricsScores: ClearingScorecard[] = [];

  transPartKey: string = 'chart.summary';

  selectedExchangeCode: string;

  allExchangeTag: string = 'ALL';

  tradeStateLabels: string[] = ['waiting', 'unallocated', 'error', 'pending', 'giveups', 'cleared', 'total'];

  availableExchanges: string[] = [];

  private clearingStatusScoresLoopSub?: any;

  constructor(cdref: ChangeDetectorRef,
              private metricsService: MetricsService,
              private appConfigService: AppConfigService,
              messageService: MessageService,
              confirmationService: ConfirmationService,
              translateService: TranslateService) {
    super(cdref, messageService, translateService);
  }

  protected initConfigs() {
    super.initConfigs();
    this.selectedExchangeCode = this.allExchangeTag;
    this.buildChartOptions();
    this.refreshChart();
  }

  ngAfterViewInit() {
    super.ngAfterViewInit();
    setTimeout(() => {
      this.startClearingStatusScoresLoop();
    });
  }

  ngOnDestroy() {
    super.ngOnDestroy();
  }

  startClearingStatusScoresLoop(): void {
    const intervalMs = this.appConfigService.chartsReloadInterval;

    this.clearingStatusScoresLoopSub?.unsubscribe();

    this.clearingStatusScoresLoopSub = timer(0, intervalMs)
      .pipe(
        takeUntil(this.destroy$),
        exhaustMap(() =>
          this.metricsService.getClearingStatusList(this.currentRequest).pipe(
            catchError(err => {
              this.errorMessage(String(err));
              return EMPTY;
            }),
          ),
        ),
      )
      .subscribe(metricsResult => {
        if (!metricsResult.success) return;

        if (metricsResult.syncMetadata?.needSync) {
          if (JSON.stringify(this.metricsScores) !== JSON.stringify(metricsResult.records)) {
            this.metricsScores = metricsResult.records;
            this.refreshChart();
            this.refreshExchangeCodes();
          }
        }

        this.currentRequest.syncMetadata = metricsResult.syncMetadata;
      });
  }

  private buildChartOptions() {
    this.chartOptions = {
      indexAxis: 'y',
      responsive: true,
      maintainAspectRatio: false,
      resizeDelay: 200,
      plugins: {
        legend: {
          labels: { color: '#dedede' }
        },
        title: {
          display: true,
          text: this.translateService.instant('page.' + this.transPartKey + '.title') + this.selectedExchangeCode,
          color: '#dedede'
        }
      },
      scales: {
        x: {
          ticks: { color: '#dedede' },
          grid: { color: 'gray' }
        },
        y: {
          ticks: { color: '#dedede' },
          grid: { color: 'rgba(255,255,255,0.1)' }
        }
      }
    };
  }

  private refreshChart() {
    const categoryLabels = this.tradeStateLabels.map(v =>
      this.translateService.instant('page.' + this.transPartKey + '.category.' + v)
    );
    const tradesData = this.getDataArray(SecurityType.TRADE);
    const ordersData = this.getDataArray(SecurityType.ORDER);
    const title = this.translateService.instant('page.' + this.transPartKey + '.title') + this.selectedExchangeCode;

    if (!this.chartData) {
      this.chartData = {
        labels: categoryLabels,
        datasets: [
          {
            label: this.translateService.instant('page.' + this.transPartKey + '.series.trades_volume'),
            data: tradesData,
            backgroundColor: 'rgba(54, 162, 235, 0.7)',
            borderColor: 'rgba(54, 162, 235, 1)',
            borderWidth: 1
          },
          {
            label: this.translateService.instant('page.' + this.transPartKey + '.series.orders_volume'),
            data: ordersData,
            backgroundColor: 'rgba(255, 159, 64, 0.7)',
            borderColor: 'rgba(255, 159, 64, 1)',
            borderWidth: 1
          }
        ]
      };
      this.chartOptions.plugins.title.text = title;
      return;
    }

    this.chartData.labels = categoryLabels;
    this.chartData.datasets[0].data = tradesData;
    this.chartData.datasets[1].data = ordersData;
    this.chartOptions.plugins.title.text = title;
    this.chartRef?.refresh();
  }

  public selectExchange(exchangeCode: string): void {
    this.selectedExchangeCode = exchangeCode;
    this.refreshChart();
  }

  getDataArray(type: SecurityType): number[] {
    const scorecards = this.metricsScores.filter(value =>
      (this.allExchangeTag === this.selectedExchangeCode
        || value.exchangeCode === this.selectedExchangeCode)
      && value.type == type);
    const result: number[] = [0, 0, 0, 0, 0, 0, 0];
    if (scorecards && scorecards.length > 0) {
      scorecards.forEach(scorecard => {
        result[0] += scorecard.totalWaiting;
        result[1] += scorecard.totalUnallocated;
        result[2] += scorecard.totalError;
        result[3] += scorecard.totalPending;
        result[4] += scorecard.totalGiveUp;
        result[5] += scorecard.totalCleared;
        result[6] += this.getSumOfTotal(scorecard);
      });
    }
    return result;
  }

  getSumOfTotal(scoreCard: ClearingScorecard): number {
    return scoreCard ?
      scoreCard.totalCleared + scoreCard.totalError + scoreCard.totalPending
      + scoreCard.totalUnallocated + scoreCard.totalWaiting : 0;
  }

  refreshExchangeCodes() {
    this.availableExchanges = [this.allExchangeTag];
    this.availableExchanges.push(...this.metricsScores
      .filter(value => value.type === SecurityType.TRADE)
      .map(value => value.exchangeCode));
  }
}
